<script setup lang="ts">
import type { Rules } from "async-validator";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useCreateUserDelegation } from "~/modules/relation/delegation/composables/useCreateUserDelegation";
import { useGetDelegationDetails } from "~/modules/relation/delegation/composables/useGetDelegationDetails";
import type { CreateUserDelegationPayload } from "~/modules/relation/delegation/models/CreateUserDelegationPayload.interface";
import type { UserDelegation } from "~/modules/relation/delegation/models/UserDelegation.interface";
import type { RelationEntity } from "~/modules/relation/models/RelationEntity.interface";
import { useFetchSectors } from "~/modules/sector/composables/useFetchSectors";
import type { Profile } from "~/modules/user/models/Profile.interface";

const props = defineProps<{
  organizationCode?: number;
  businessUnitCode?: number;
  userLogin?: string;
  receivedDelegations?: RelationEntity[] | null;
  userDelegation?: UserDelegation;
}>();

const emits = defineEmits(["cancel", "success"]);
const { t } = useI18n();

const userDelegationFormRef = ref<HTMLFormElement | undefined | null>();
const isFormValid: ComputedRef<boolean | undefined> = computed(
  () => userDelegationFormRef.value?.isValid,
);

const userDelegationForm: Partial<CreateUserDelegationPayload> = reactive({
  sourceOrganizationCode: undefined,
  login: "",
  profileId: undefined,
  sectorCodes: [],
});

const rules: Rules = {
  sourceOrganizationCode: [{ required: true }],
  profileId: [{ required: true }],
};

const { isLoading, error, createUserDelegation } = useCreateUserDelegation({
  organizationCode: props.organizationCode,
  businessUnitCode: props.businessUnitCode,
});

const {
  isLoading: isLoadingSectors,
  error: errorFetchSectors,
  sector,
  setOrganizationCode: setFetchSectorsOrganizationCode,
  fetchSectors,
} = useFetchSectors();

const {
  isLoading: isLoadingDelegationDetails,
  error: errorDelegationDetails,
  result: delegationDetails,
  setOrganizationCode: setGetDelegationDetailsOrganizationCode,
  getDelegationDetails,
} = useGetDelegationDetails();

const sectorCodesAsString = computed({
  get: () => userDelegationForm.sectorCodes?.map((code) => String(code)),
  set: (value: string[] | undefined) => {
    userDelegationForm.sectorCodes = value?.map((code) => Number(code));
  },
});

const delegableProfiles: ComputedRef<Profile[]> = computed(() => {
  return (
    delegationDetails.value?.profiles?.filter(
      (profile) => profile.canBeDelegated,
    ) ?? []
  );
});

function initializeForm() {
  userDelegationForm.sourceOrganizationCode = undefined;
  userDelegationForm.login = props.userLogin;
  userDelegationForm.profileId = undefined;
  userDelegationForm.sectorCodes = [];

  if (props.userDelegation) {
    userDelegationForm.sourceOrganizationCode =
      props.userDelegation.sourceOrganizationCode;
    userDelegationForm.profileId = props.userDelegation.profileId;
    userDelegationForm.sectorCodes = props.userDelegation.sectorCodes;
  }
}

async function onSubmit() {
  if (isFormValid.value && props.userLogin) {
    const createUserDelegationPayload: CreateUserDelegationPayload = {
      sourceOrganizationCode: userDelegationForm.sourceOrganizationCode!,
      login: props.userLogin,
      profileId: userDelegationForm.profileId!,
      sectorCodes: userDelegationForm.sectorCodes,
    };

    await createUserDelegation(props.userLogin, createUserDelegationPayload);

    if (error.value) {
      error.value.notify(NotificationKey.saveError);
    } else {
      showNotificationSuccess(t(NotificationKey.saveSuccess));
      emits("success");
    }
  }
}

function onCancel() {
  emits("cancel");
}

onMounted(initializeForm);

watch(
  () => userDelegationForm.sourceOrganizationCode,
  async () => {
    if (userDelegationForm.sourceOrganizationCode) {
      userDelegationForm.profileId = undefined;
      userDelegationForm.sectorCodes = [];

      setFetchSectorsOrganizationCode(
        userDelegationForm.sourceOrganizationCode,
      );
      await fetchSectors(true);

      setGetDelegationDetailsOrganizationCode(
        userDelegationForm.sourceOrganizationCode,
      );
      await getDelegationDetails(
        (props.organizationCode ?? props.businessUnitCode)!,
      );
    }
  },
);

watch(
  () => props.userDelegation,
  () => initializeForm(),
  { immediate: true },
);
</script>

<template>
  <div class="p-4 border border-gray-400 rounded-lg bg-white">
    <lxc-form
      ref="userDelegationFormRef"
      :model="userDelegationForm"
      :rules="rules"
      class="flex flex-col gap-2"
      @submit.prevent
    >
      <div class="flex">
        <lxc-select
          v-model="userDelegationForm.sourceOrganizationCode"
          :placeholder="t('user.form.delegations.selectOrganization')"
        >
          <lxc-option
            v-for="delegation in receivedDelegations"
            :key="delegation.organizationCode"
            :value="delegation.organizationCode"
            :label="delegation.organizationName"
          />
        </lxc-select>
      </div>
      <container-component
        v-if="userDelegationForm.sourceOrganizationCode"
        :px="0"
        :py="0"
        :is-loading="isLoadingSectors || isLoadingDelegationDetails"
        :error="errorDelegationDetails"
        :loader-size="10"
        :loader-container-class="['!mt-4']"
      >
        <div class="flex justify-between flex-1">
          <div class="flex gap-2 w-2/3">
            <user-sector-selection
              v-model="sectorCodesAsString"
              :sector="sector"
              :error="errorFetchSectors"
              class="flex-1"
            />
            <lxc-select
              v-model="userDelegationForm.profileId"
              :placeholder="$t('user.form.delegations.selectProfile')"
              :error="errorDelegationDetails"
              :select-no-result-text="$t('user.form.delegations.noProfile')"
            >
              <lxc-option
                v-for="profile in delegableProfiles"
                :key="profile.id"
                :value="profile.id"
                :label="profile.name"
              />
              <lxc-option
                v-if="delegableProfiles.length === 0"
                :label="$t('user.form.delegations.noProfile')"
                value=""
                disabled
              />
            </lxc-select>
          </div>
          <div class="flex flex-row-reverse gap-2">
            <lxc-button
              :title="t('button.validate')"
              :text="t('button.validate')"
              type="secondary"
              :is-loading="isLoading"
              :disabled="!isFormValid"
              @click="onSubmit()"
            />
            <lxc-button
              :title="t('button.cancel')"
              :text="t('button.cancel')"
              type="borderless"
              class="text-primary active:text-primary-900 enabled:hover:text-primary-800"
              @click="onCancel"
            />
          </div>
        </div>
      </container-component>
    </lxc-form>
  </div>
</template>
